import type {
  Baggage,
  ContextPropagation,
  TraceFlags as TraceFlagsType,
  TransportBaggage,
} from './tel-types'
import { TraceFlags } from './tel-types'
import { ulidToSpanId, ulidToTraceId } from './otel-exporter/convert-id'

const NATIVE_TRACEPARENT_RE =
  /^00-([0-9a-zA-Z_]{1,52})-([0-9a-zA-Z_]{1,52})-([0-9a-f]{2})$/
const W3C_TRACEPARENT_RE = /^00-([0-9a-f]{32})-([0-9a-f]{16})-([0-9a-f]{2})$/

const MODE_HEADER = 'x-tel-mode'

/** Worker carrier of a `nullTel` caller: W3C sampled bit 0 (OTel ParentBased). */
export const NOT_SAMPLED_BAGGAGE: TransportBaggage = Object.freeze({ traceFlags: TraceFlags.NONE })

/** Constant headers a `nullTel` caller sends so the callee runs under `nullTel` too. */
export const NOT_SAMPLED_HEADERS: Readonly<Record<ContextPropagation, Readonly<Record<string, string>>>> = Object.freeze({
  w3c: Object.freeze({ traceparent: '00-00000000000000000000000000000001-0000000000000001-00', [MODE_HEADER]: 'w3c' }),
  native: Object.freeze({ traceparent: '00-0-0-00', [MODE_HEADER]: 'native' }),
})

export function isNotSampled(baggage: TransportBaggage | null | undefined): boolean {
  return baggage?.traceFlags === TraceFlags.NONE
}

const VENDOR_KEY = 'w'
const EMPTY_TRACE_ID = '00000000000000000000000000000000'
const EMPTY_SPAN_ID = '0000000000000000'

export interface BaggagePropagationOpts {
  mode?: ContextPropagation
}

/**
 * Serialize Baggage -> HTTP headers.
 * Default W3C mode emits standard headers and carries native ULIDs in vendor
 * tracestate. Native mode preserves native ULIDs in the same headers. The
 * x-tel-mode marker lets controlled consumers reject a configuration mismatch.
 */
export function serializeBaggage(
  baggage: Baggage,
  opts: BaggagePropagationOpts = {},
): Record<string, string> {
  const { requestId: _requestId, ...transport } = baggage
  const mode = opts.mode ?? 'w3c'
  const headers =
    mode === 'native'
      ? serializeNativeBaggage(transport)
      : serializeW3cBaggage(transport)
  if (Object.keys(headers).length > 0) headers[MODE_HEADER] = mode
  return headers
}

/**
 * Parse HTTP headers -> Baggage.
 * x-tel-mode identifies the sender's mode. Receiving a mode other than the
 * configured mode throws instead of silently dropping context.
 */
export function parseBaggage(
  headers: Headers,
  opts: BaggagePropagationOpts = {},
): TransportBaggage | undefined {
  const mode = opts.mode ?? 'w3c'
  assertConfiguredMode(headers, mode)
  if (mode === 'native') return parseNativeBaggage(headers)
  return parseW3cBaggage(headers)
}

function serializeNativeBaggage(
  baggage: TransportBaggage,
): Record<string, string> {
  const headers: Record<string, string> = {}

  if (baggage.baggage) headers.baggage = baggage.baggage

  if (baggage.traceId && baggage.parentId) {
    const flags = formatTraceFlags(baggage.traceFlags)
    headers.traceparent = `00-${baggage.traceId}-${baggage.parentId}-${flags}`
  }

  const wParts: string[] = []
  if (baggage.sessionId) wParts.push(`sessionId:${baggage.sessionId}`)

  const wEntry = wParts.length > 0 ? `${VENDOR_KEY}=${wParts.join(';')}` : ''
  const other = baggage.traceState ?? ''

  const parts = [wEntry, other].filter(Boolean)
  if (parts.length > 0) headers.tracestate = parts.join(',')

  return headers
}

function parseNativeBaggage(
  headers: Headers,
): TransportBaggage | undefined {
  const traceparent = headers.get('traceparent')
  if (!traceparent) return parseStandardOnlyBaggage(headers)

  const match = traceparent.match(NATIVE_TRACEPARENT_RE)
  if (!match) return parseStandardOnlyBaggage(headers)

  const traceId = match[1]
  const parentId = match[2]
  const traceFlags = Number.parseInt(match[3] ?? '00', 16) as TraceFlagsType

  const baggage: Baggage = { traceId, parentId, traceFlags }
  applyStandardBaggage(baggage, headers)
  const traceState = parseTraceState(headers.get('tracestate'))
  applyNativeVendorState(baggage, traceState.vendor)
  if (traceState.foreign.length > 0)
    baggage.traceState = traceState.foreign.join(',')

  return baggage
}

function serializeW3cBaggage(
  baggage: TransportBaggage,
): Record<string, string> {
  const headers: Record<string, string> = {}
  if (baggage.baggage) headers.baggage = baggage.baggage

  const spanId = baggage.parentId
    ? tryUlidToSpanId(baggage.parentId)
    : undefined
  const traceId = resolveW3cTraceId(baggage)

  if (traceId && spanId) {
    headers.traceparent = `00-${traceId}-${spanId}-${formatTraceFlags(
      baggage.traceFlags,
    )}`
  }

  const traceState = parseTraceState(baggage.traceState)
  const vendor = serializeW3cVendorState(baggage)
  const entries = [vendor, ...traceState.foreign].filter(Boolean)
  if (entries.length > 0) headers.tracestate = entries.join(',')

  return headers
}

function parseW3cBaggage(headers: Headers): TransportBaggage | undefined {
  const traceparent = headers.get('traceparent')
  if (!traceparent) return parseStandardOnlyBaggage(headers)

  const match = traceparent.match(W3C_TRACEPARENT_RE)
  if (!match) return parseStandardOnlyBaggage(headers)

  const traceId = match[1]
  const parentSpanId = match[2]
  if (!isValidW3cTraceId(traceId) || !isValidW3cSpanId(parentSpanId)) {
    return parseStandardOnlyBaggage(headers)
  }

  const baggage: Baggage = {
    traceFlags: Number.parseInt(match[3] ?? '00', 16),
    otel: { traceId, parentSpanId },
  }
  applyStandardBaggage(baggage, headers)

  const traceState = parseTraceState(headers.get('tracestate'))
  applyW3cVendorState(baggage, traceState.vendor)
  if (traceState.foreign.length > 0)
    baggage.traceState = traceState.foreign.join(',')

  return baggage
}

function applyStandardBaggage(baggage: Baggage, headers: Headers): void {
  const standardBaggage = headers.get('baggage')
  if (standardBaggage) baggage.baggage = standardBaggage
}

function parseStandardOnlyBaggage(headers: Headers): Baggage | undefined {
  const standardBaggage = headers.get('baggage')
  if (!standardBaggage) return undefined
  return { baggage: standardBaggage }
}

function assertConfiguredMode(
  headers: Headers,
  configured: ContextPropagation,
): void {
  const received = headers.get(MODE_HEADER)

  if (received && received !== configured) {
    throw new Error(
      `Context propagation mismatch: configured "${configured}", received "${received}"`,
    )
  }
}

function resolveW3cTraceId(baggage: Baggage): string | undefined {
  if (baggage.otel?.traceId && isValidW3cTraceId(baggage.otel.traceId)) {
    return baggage.otel.traceId
  }
  if (!baggage.traceId) return undefined
  return tryUlidToTraceId(baggage.traceId)
}

function serializeW3cVendorState(baggage: Baggage): string {
  const wParts: string[] = []
  if (baggage.traceId) wParts.push(`trace:${baggage.traceId}`)
  if (baggage.parentId) wParts.push(`span:${baggage.parentId}`)
  if (baggage.sessionId) wParts.push(`session:${baggage.sessionId}`)
  return wParts.length > 0 ? `${VENDOR_KEY}=${wParts.join(';')}` : ''
}

function applyNativeVendorState(
  baggage: Baggage,
  vendorState: string | undefined,
): void {
  const vendor = parseVendorValue(vendorState)
  const sessionId = vendor.get('sessionId') ?? vendor.get('session')
  if (sessionId) baggage.sessionId = sessionId
}

function applyW3cVendorState(
  baggage: Baggage,
  vendorState: string | undefined,
): void {
  const vendor = parseVendorValue(vendorState)
  const traceId = vendor.get('trace') ?? vendor.get('traceId')
  const parentId =
    vendor.get('span') ?? vendor.get('spanId') ?? vendor.get('parentId')
  const sessionId = vendor.get('session') ?? vendor.get('sessionId')

  if (traceId) baggage.traceId = traceId
  if (parentId) baggage.parentId = parentId
  if (sessionId) baggage.sessionId = sessionId
}

function parseTraceState(traceState: string | null | undefined): {
  vendor?: string
  foreign: string[]
} {
  if (!traceState) return { foreign: [] }

  const entries = traceState
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
  const vendorEntry = entries.find((entry) =>
    entry.startsWith(`${VENDOR_KEY}=`),
  )
  const foreign = entries.filter((entry) => !entry.startsWith(`${VENDOR_KEY}=`))

  return {
    vendor: vendorEntry?.slice(VENDOR_KEY.length + 1),
    foreign,
  }
}

function parseVendorValue(value: string | undefined): Map<string, string> {
  const result = new Map<string, string>()
  if (!value) return result

  for (const part of value.split(';')) {
    const [key, ...valueParts] = part.split(':')
    const joined = valueParts.join(':')
    if (key && joined) result.set(key, joined)
  }

  return result
}

function formatTraceFlags(flags: number | undefined): string {
  // A recording caller samples (W3C sampled bit 1); only nullTel sends 00.
  return (flags ?? TraceFlags.SAMPLED).toString(16).padStart(2, '0').slice(-2)
}

function isValidW3cTraceId(traceId: string): boolean {
  return /^[0-9a-f]{32}$/.test(traceId) && traceId !== EMPTY_TRACE_ID
}

function isValidW3cSpanId(spanId: string): boolean {
  return /^[0-9a-f]{16}$/.test(spanId) && spanId !== EMPTY_SPAN_ID
}

function tryUlidToTraceId(id: string): string | undefined {
  try {
    const traceId = ulidToTraceId(id)
    return isValidW3cTraceId(traceId) ? traceId : undefined
  } catch {
    return undefined
  }
}

function tryUlidToSpanId(id: string): string | undefined {
  try {
    const spanId = ulidToSpanId(id)
    return isValidW3cSpanId(spanId) ? spanId : undefined
  } catch {
    return undefined
  }
}
